import { Checkbox, IconButton } from "@mui/material";
import { Alert, AlertOccurrence } from "interfaces/alert";
import { CellProps, Column, HeaderProps, SortByFn } from "react-table";
import DeleteIcon from "@mui/icons-material/Close";
import { SelectAllCheckbox } from "components/SelectAllCheckbox";
import { DateWithTooltip } from "components/DateWithTooltip";
import { size } from "lodash";
import { CombinedCell } from "components/SharedTable/tableRenderers";
import { PropsWithChildren } from "react";
import { styled } from "@mui/material/styles";

export interface AlertTableColumnsProps {
  alertsTableData: Alert[];
  selectedOnCurrentPage: any[];
  getIsAlertSelected: (alertId: number) => boolean | undefined;
  toggleSelectRows: (
    newAlert: Alert,
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => void;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickDismissAlerts: (alertToDismiss: Alert[]) => void;
}

// Custom Sorting function for the Site/Machine column.
// localeCompare() method returns a number indicating whether a reference string comes before, or after, or is the same as the given string in sort order.
const sortSiteMachine: SortByFn<Alert> = (rowA, rowB) => {
  const siteComparison = rowA.values.site_machine[0].localeCompare(
    rowB.values.site_machine[0]
  );
  if (siteComparison !== 0) {
    return siteComparison;
  }
  return rowA.values.site_machine[1].localeCompare(rowB.values.site_machine[1]);
};

export const ALERT_TABLE_COLUMNS = ({
  alertsTableData,
  selectedOnCurrentPage,
  getIsAlertSelected,
  toggleSelectRows,
  handleSelectAll,
  onClickDismissAlerts
}: AlertTableColumnsProps): Column<Alert>[] => [
  {
    Header: (
      <SelectAllCheckbox
        options={alertsTableData}
        selectedOptions={selectedOnCurrentPage}
        optionComparer={(option1, option2) =>
          option1.alert_id === option2.alert_id
        }
        onChange={handleSelectAll}
      />
    ),
    id: "selection",
    Cell: (cellProps: CellProps<Alert, AlertOccurrence[]>) => (
      <Checkbox
        color="primary"
        checked={getIsAlertSelected(cellProps.row.original.alert_id)}
        onChange={event =>
          toggleSelectRows(
            cellProps.row.original,
            cellProps.row.index,
            event,
            cellProps.row.id
          )
        }
        size="small"
      />
    ),
    disableSortBy: true
  },
  {
    Header: "Severity",
    id: "severity_type__id",
    accessor: data => data.severity_type_name
  },
  {
    Header: (
      <div>
        <Header>{"Site"}</Header>
        <SubHeader>{"Machine"}</SubHeader>
      </div>
    ),
    id: "site_machine",
    accessor: data => [data.site_name, data.machine_name],
    sortType: sortSiteMachine,
    Cell: (data: PropsWithChildren<HeaderProps<Alert>>) =>
      CombinedCell({ value: data.cell.value })
  },
  {
    Header: "Component",
    id: "rule__tag__component__name",
    accessor: data => data.component_name
  },
  {
    Header: "Rule",
    id: "rule__name",
    accessor: data => data.rule_name
  },
  {
    Header: "Date Raised",
    id: "latest_period__start_date",
    accessor: data => data.period_start_date,
    Cell: (cellProps: CellProps<Alert>) => (
      <DateWithTooltip date={cellProps.value} />
    )
  },
  {
    Header: () => (
      <AlertsActions
        selectedOnCurrentPage={selectedOnCurrentPage}
        onClickDismissAlerts={onClickDismissAlerts}
      />
    ),
    id: "dismiss",
    disableSortBy: true,
    Cell: (cellProps: CellProps<Alert>) => (
      <IconButton
        title="Dismiss alert"
        onClick={e => {
          e.stopPropagation();
          onClickDismissAlerts([cellProps.row.original]);
        }}
        size="large"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    )
  }
];

interface AlertsActionsProps {
  selectedOnCurrentPage: any[];
  onClickDismissAlerts: (alertToDismiss: Alert[]) => void;
}

const AlertsActions = ({
  selectedOnCurrentPage,
  onClickDismissAlerts
}: AlertsActionsProps): JSX.Element => {
  if (size(selectedOnCurrentPage) <= 1) return <>Actions</>;

  return (
    <IconButton
      title="Dismiss all selected"
      onClick={() => onClickDismissAlerts(selectedOnCurrentPage)}
      size="large"
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

const Header = styled("div")`
  font-weight: bold;
  white-space: pre-wrap;
`;

const SubHeader = styled("div")`
  font-weight: normal;
`;
